import React, { useEffect, useState } from 'react';
import { Button, Table, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InsuranceCompanyAgent, Company } from '../interfaces/InsuranceCompanyAgent';
import { getAgentsByUser, createAgent, updateAgent, deleteAgent } from '../utils/api/insuranceCompanyAgent';
import Select from 'react-select';
import globalErrorHandler from '../utils/globalErrorHandler';
import CompanyAutocomplete from './CompanyAutocomplete';

interface InsuranceCompanyAgentManagerProps {
    userId?: string;
    canEdit: boolean;
}

const agentIdTypeOptions = [
    { value: 'pension', label: 'Pension' },
    { value: 'provident', label: 'Provident' },
    { value: 'insurance', label: 'Insurance' },
];

const InsuranceCompanyAgentManager: React.FC<InsuranceCompanyAgentManagerProps> = ({ userId, canEdit }) => {
    const [agents, setAgents] = useState<InsuranceCompanyAgent[]>([]);
    const [loading, setLoading] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [editingAgentId, setEditingAgentId] = useState<string | null>(null);

    // Form state
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [selectedAgentIdType, setSelectedAgentIdType] = useState<{ value: string; label: string } | null>(null);
    const [agentIdInput, setAgentIdInput] = useState('');

    useEffect(() => {
        if (userId) {
            loadAgents();
        }
    }, [userId]);

    const loadAgents = async () => {
        if (!userId) return;
        try {
            setLoading(true);
            const response = await getAgentsByUser(userId);
            setAgents(response.agents || []);
        } catch (error) {
            globalErrorHandler(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setIsAddingNew(true);
        setEditingAgentId(null);
        resetForm();
    };

    const handleEdit = (agent: InsuranceCompanyAgent) => {
        if (!agent._id) return;
        setEditingAgentId(agent._id);
        setIsAddingNew(false);
        setSelectedCompany(agent.company);
        setSelectedAgentIdType(agentIdTypeOptions.find((opt) => opt.value === agent.agentIdType) || null);
        setAgentIdInput(agent.agentId);
    };

    const handleDelete = async (agentEntryId: string) => {
        if (!window.confirm('Are you sure you want to delete this agent ID entry?')) return;

        try {
            await deleteAgent(agentEntryId);
            setAgents(agents.filter((a) => a._id !== agentEntryId));
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    const resetForm = () => {
        setSelectedCompany(null);
        setSelectedAgentIdType(null);
        setAgentIdInput('');
    };

    const handleSave = async () => {
        if (!selectedCompany || !selectedAgentIdType || !agentIdInput || !userId) {
            alert('Please fill in all fields');
            return;
        }

        try {
            if (editingAgentId) {
                // Update existing
                const response = await updateAgent(editingAgentId, {
                    companyId: selectedCompany._id,
                    agentIdType: selectedAgentIdType.value,
                    agentId: agentIdInput,
                });
                const updatedAgents = agents.map((a) => (a._id === editingAgentId ? response.agent : a));
                setAgents(updatedAgents);
            } else {
                // Create new
                const response = await createAgent({
                    userId,
                    companyId: selectedCompany._id,
                    agentIdType: selectedAgentIdType.value,
                    agentId: agentIdInput,
                });
                setAgents([response.agent, ...agents]);
            }
            handleCancel();
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    const handleCancel = () => {
        setIsAddingNew(false);
        setEditingAgentId(null);
        resetForm();
    };

    if (!userId) {
        return <div className="alert alert-info">Please save the user first before managing insurance company agent IDs.</div>;
    }

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5>Insurance Company Agent IDs</h5>
                {canEdit && !isAddingNew && !editingAgentId && (
                    <Button size="sm" variant="primary" onClick={handleAddNew}>
                        Add Agent ID
                    </Button>
                )}
            </div>

            {loading ? (
                <div>Loading agent IDs...</div>
            ) : (
                <Table striped bordered hover size="sm">
                    <thead>
                        <tr>
                            <th style={{ width: '35%' }}>Company</th>
                            <th style={{ width: '20%' }}>Type</th>
                            <th style={{ width: '25%' }}>Agent ID</th>
                            {canEdit && <th style={{ width: '20%' }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isAddingNew && (
                            <tr className="table-active">
                                <td>
                                    <CompanyAutocomplete
                                        value={selectedCompany}
                                        onSelect={setSelectedCompany}
                                        placeholder="Search company..."
                                        disabled={!canEdit}
                                    />
                                </td>
                                <td>
                                    <Select
                                        value={selectedAgentIdType}
                                        onChange={setSelectedAgentIdType}
                                        options={agentIdTypeOptions}
                                        placeholder="Select type..."
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </td>
                                <td>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        value={agentIdInput}
                                        onChange={(e) => setAgentIdInput(e.target.value)}
                                        placeholder="Enter agent ID..."
                                    />
                                </td>
                                <td>
                                    <OverlayTrigger placement="top" overlay={<Tooltip id="save-tooltip">Save</Tooltip>}>
                                        <i
                                            className="mdi mdi-check-circle-outline cursor-pointer text-success me-2"
                                            style={{ fontSize: '1.3rem' }}
                                            onClick={handleSave}
                                        ></i>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip id="cancel-tooltip">Cancel</Tooltip>}>
                                        <i
                                            className="mdi mdi-close-circle-outline cursor-pointer text-secondary"
                                            style={{ fontSize: '1.3rem' }}
                                            onClick={handleCancel}
                                        ></i>
                                    </OverlayTrigger>
                                </td>
                            </tr>
                        )}
                        {agents.map((agent) => (
                            <tr key={agent._id} className={editingAgentId === agent._id ? 'table-active' : ''}>
                                {editingAgentId === agent._id ? (
                                    <>
                                        <td>
                                            <CompanyAutocomplete
                                                value={selectedCompany}
                                                onSelect={setSelectedCompany}
                                                placeholder="Search company..."
                                                disabled={!canEdit}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                value={selectedAgentIdType}
                                                onChange={setSelectedAgentIdType}
                                                options={agentIdTypeOptions}
                                                placeholder="Select type..."
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={agentIdInput}
                                                onChange={(e) => setAgentIdInput(e.target.value)}
                                                placeholder="Enter agent ID..."
                                            />
                                        </td>
                                        <td>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="save-tooltip">Save</Tooltip>}>
                                                <i
                                                    className="mdi mdi-check-circle-outline cursor-pointer text-success me-2"
                                                    style={{ fontSize: '1.3rem' }}
                                                    onClick={handleSave}
                                                ></i>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="cancel-tooltip">Cancel</Tooltip>}>
                                                <i
                                                    className="mdi mdi-close-circle-outline cursor-pointer text-secondary"
                                                    style={{ fontSize: '1.3rem' }}
                                                    onClick={handleCancel}
                                                ></i>
                                            </OverlayTrigger>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{agent.company.name}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{agent.agentIdType}</td>
                                        <td>{agent.agentId}</td>
                                        {canEdit && (
                                            <td>
                                                <OverlayTrigger placement="top" overlay={<Tooltip id="edit-tooltip">Edit</Tooltip>}>
                                                    <i
                                                        className="mdi mdi-square-edit-outline cursor-pointer text-primary me-2"
                                                        style={{ fontSize: '1.3rem' }}
                                                        onClick={() => handleEdit(agent)}
                                                    ></i>
                                                </OverlayTrigger>
                                                <OverlayTrigger placement="top" overlay={<Tooltip id="delete-tooltip">Delete</Tooltip>}>
                                                    <i
                                                        className="mdi mdi-delete cursor-pointer text-danger"
                                                        style={{ fontSize: '1.3rem' }}
                                                        onClick={() => agent._id && handleDelete(agent._id)}
                                                    ></i>
                                                </OverlayTrigger>
                                            </td>
                                        )}
                                    </>
                                )}
                            </tr>
                        ))}
                        {!isAddingNew && agents.length === 0 && (
                            <tr>
                                <td colSpan={canEdit ? 4 : 3} className="text-center text-muted">
                                    No agent IDs configured for this user.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default InsuranceCompanyAgentManager;
